/**
 * 教材相关API
 */
import apiClient, { rawApiClient } from './client'

export interface TextbookInfo {
  name: string
  tableName: string
  educationLevel?: string
  category?: string
}

export interface TextbookListResponse {
  textbooks: TextbookInfo[]
  total: number
}

/**
 * 从SQL文件获取所有教材列表
 */
export async function getTextbookList(): Promise<TextbookListResponse> {
  try {
    const response = await apiClient.get<TextbookListResponse>('/v1/textbooks/list')
    // apiClient 的响应拦截器已经返回了 response.data
    // 确保响应格式正确
    if (response && typeof response === 'object' && 'textbooks' in response) {
      return response as TextbookListResponse
    }
    // 如果响应格式不正确，尝试从 data 属性获取
    const data = (response as any)?.data || response
    if (data && typeof data === 'object' && 'textbooks' in data) {
      return data as TextbookListResponse
    }
    throw new Error('响应格式异常: ' + JSON.stringify(response))
  } catch (error: any) {
    console.error('[Textbooks API] 获取教材列表失败:', error)
    // 如果请求失败，返回空列表而不是抛出错误
    if (error.response) {
      console.error('[Textbooks API] 响应错误:', error.response.status, error.response.data)
      throw new Error(error.response.data?.detail || error.response.data?.message || '获取教材列表失败')
    }
    if (error.message) {
      throw error
    }
    throw new Error('网络请求失败: ' + (error.message || '未知错误'))
  }
}

/**
 * 获取教材详情（根据表名）
 */
export async function getTextbookByTableName(tableName: string): Promise<any> {
  const response = await apiClient.get(`/v1/textbooks/${tableName}`)
  return response
}

/**
 * 获取教材章节列表
 */
export async function getTextbookChapters(
  tableName: string,
  skip: number = 0,
  limit: number = 100
): Promise<any> {
  const response = await apiClient.get(`/v1/textbooks/${tableName}/chapters`, {
    params: { skip, limit }
  })
  return response
}

/**
 * 启动教材编写工作流
 */
export interface StartWritingRequest {
  chatInput: string
  textbookName?: string
  category?: string
  educationLevel?: string
  /** 参考文档文件列表，将转为 base64 在 JSON 中发送（避免后端 multipart 依赖） */
  referenceFiles?: File[]
}

export interface StartWritingResponse {
  success: boolean
  message: string
  stage?: string  // 'outline_generated', 'outline_confirmed', 'chapter_generating', 'completed'
  bookOutline?: any
  outlineMarkdown?: string
  taskId?: string
  workflowId?: string
  suggestions?: string[]  // 错误建议
  error_details?: any  // 错误详情
}

export interface ConfirmOutlineRequest {
  bookOutline: any
  modifications?: string
  confirm: boolean
}

export interface ConfirmOutlineResponse {
  success: boolean
  message: string
  stage: string
  bookOutline?: any
  outlineMarkdown?: string
  outlineDocumentUrl?: string
}

export interface GenerateChapterRequest {
  bookOutline: any
  chapterNumber: number
  chapterTitle: string
  sections: string[]
}

export interface GenerateChapterResponse {
  success: boolean
  message: string
  stage: string
  chapterNumber: number
  chapterTitle: string
  chapterContent?: string
  chapterMarkdown?: string
}

export interface ConfirmChapterRequest {
  chapterNumber: number
  chapterContent: string
  modifications?: string
  confirm: boolean
  continueNext: boolean
  bookOutline?: any  // 大纲信息，用于重新生成章节
}

export interface ConfirmChapterResponse {
  success: boolean
  message: string
  stage: string
  chapterNumber?: number
  chapterContent?: string
  chapterMarkdown?: string
  nextChapterNumber?: number
  completed: boolean
  bookDocumentUrl?: string
}

/** 将 File 转为 base64 字符串 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      const base64 = result.includes(',') ? result.split(',')[1] : result
      resolve(base64 || '')
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

/**
 * 仅从 docx/doc 等提取纯文本（教材服务 extract-document），不调用 n8n 大纲工作流。
 */
export async function extractWritingDocumentText(
  file: File,
  options?: { signal?: AbortSignal }
): Promise<{ text: string; filename: string }> {
  const form = new FormData()
  form.append('file', file)
  const response = (await apiClient.post(
    '/v1/textbooks/writing/extract-document',
    form,
    { timeout: 120000, signal: options?.signal }
  )) as {
    text?: string
    referenceDocumentText?: string
    filename?: string
    success?: boolean
  }
  const text = (response.referenceDocumentText || response.text || '').trim()
  return {
    text,
    filename: response.filename || file.name,
  }
}

/**
 * 将目录/提纲纯文本转为最小 bookOutline，供「撰写书籍内容」直连 Book Chapter Generation 使用（不经大纲 n8n）。
 */
export function buildMinimalBookOutlineFromTocText(raw: string, bookTitleFallback: string) {
  const lines = raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !/^目\s*录\s*$/.test(l))

  const fallback = bookTitleFallback || '教材'
  let bookTitle = fallback
  if (lines.length > 0 && lines[0].length <= 120) {
    bookTitle = lines[0]
  }

  const bodyLines = lines.length > 1 ? lines.slice(1) : lines
  const maxSections = 150
  const sections = bodyLines.slice(0, maxSections).map((line, idx) => ({
    section_number: idx + 1,
    section_title: line.replace(/^[\s\d一二三四五六七八九十百千、．.]+/, '').trim() || line,
    items: [] as string[],
  }))

  const finalSections =
    sections.length > 0
      ? sections
      : [{ section_number: 1, section_title: '全文', items: [] as string[] }]

  return {
    title: bookTitle,
    author: '',
    target_audience: '',
    book_goals: '',
    introduction: { sections: [] as { section_title?: string; items?: string[] }[] },
    chapters: [
      {
        chapter_number: 1,
        chapter_title: '正文',
        sections: finalSections,
      },
    ],
  }
}

export async function startTextbookWriting(
  request: StartWritingRequest,
  options?: { signal?: AbortSignal }
): Promise<StartWritingResponse> {
  try {
    const hasFiles = request.referenceFiles && request.referenceFiles.length > 0
    console.log('[Textbooks API] 调用startTextbookWriting:', { ...request, referenceFilesCount: request.referenceFiles?.length ?? 0 })

    const body: Record<string, unknown> = {
      chatInput: request.chatInput,
      textbookName: request.textbookName,
      category: request.category,
      educationLevel: request.educationLevel
    }
    if (hasFiles) {
      const file = request.referenceFiles![0]
      body.referenceDocumentBase64 = await fileToBase64(file)
      body.referenceDocumentFilename = file.name
    }

    const response = await apiClient.post<StartWritingResponse>(
      '/v1/textbooks/writing/start',
      body,
      { timeout: 600000, signal: options?.signal }
    )
    console.log('[Textbooks API] startTextbookWriting响应:', response)
    return response
  } catch (error: any) {
    console.error('[Textbooks API] startTextbookWriting失败:', error)
    // 如果API返回了响应数据（即使status不是200），尝试提取错误信息
    if (error.response?.data) {
      // 如果响应数据包含success字段，说明是业务逻辑错误，返回响应数据
      if (error.response.data.success !== undefined) {
        return error.response.data as StartWritingResponse
      }
      // 否则抛出错误
      throw new Error(error.response.data.detail || error.response.data.message || '启动编写失败')
    }
    // 如果是网络错误或其他错误，重新抛出
    throw error
  }
}

export async function confirmOutline(
  request: ConfirmOutlineRequest,
  options?: { signal?: AbortSignal }
): Promise<ConfirmOutlineResponse> {
  const response = await apiClient.post<ConfirmOutlineResponse>(
    '/v1/textbooks/writing/confirm-outline',
    request,
    {
      timeout: 300000, // 5分钟
      signal: options?.signal,
    }
  )
  return response
}

export async function generateChapter(
  request: GenerateChapterRequest,
  options?: { signal?: AbortSignal }
): Promise<GenerateChapterResponse> {
  const response = await apiClient.post<GenerateChapterResponse>(
    '/v1/textbooks/writing/generate-chapter',
    request,
    {
      timeout: 600000, // 10分钟
      signal: options?.signal,
    }
  )
  return response
}

export interface ChapterStatusResponse {
  chapterNumber: number
  chapterTitle?: string
  chapterContent?: string
  chapterMarkdown?: string
  bookTitle?: string
  author?: string
  bookOutline?: any
  stage: string
  success: boolean
  message?: string
  timestamp?: string
}

export async function getChapterStatus(
  chapterNumber: number
): Promise<ChapterStatusResponse> {
  const response = await apiClient.get<ChapterStatusResponse>(
    `/v1/textbooks/writing/chapter-status/${chapterNumber}`
  )
  return response
}

export async function confirmChapter(
  request: ConfirmChapterRequest,
  options?: { signal?: AbortSignal }
): Promise<ConfirmChapterResponse> {
  const response = await apiClient.post<ConfirmChapterResponse>(
    '/v1/textbooks/writing/confirm-chapter',
    request,
    {
      timeout: 60000, // 1分钟
      signal: options?.signal,
    }
  )
  return response
}

export interface DownloadDocumentRequest {
  content?: string
  title?: string
  author?: string
  chapters?: Array<{
    chapterNumber: number
    chapterTitle: string
    chapterContent: string
    chapterMarkdown?: string
  }>
  outlineMarkdown?: string  // 大纲内容（用于完整书籍）
}

// 教材和章节管理API
export interface Textbook {
  id: number
  title: string
  subject: string
  version: string
  status: string
  created_at: string
}

export interface Chapter {
  id: number
  textbook_id: number
  chapter_number: number
  title: string
  content_id: string
  status: string
  created_at: string
}

export interface Review {
  id: number
  chapter_id: number
  reviewer_id: number
  comment: string
  status: string
  created_at: string
}

// 创建教材
export async function createTextbook(data: { title: string; subject: string; description?: string }): Promise<Textbook> {
  const response = await apiClient.post<Textbook>('/v1/textbooks/', data)
  return response
}

// 获取教材列表（从数据库）
export async function getTextbooksFromDB(skip: number = 0, limit: number = 100): Promise<Textbook[]> {
  const response = await apiClient.get<Textbook[]>('/v1/textbooks/', { params: { skip, limit } })
  return response
}

// 获取教材详情
export async function getTextbookById(textbookId: number): Promise<Textbook> {
  const response = await apiClient.get<Textbook>(`/v1/textbooks/${textbookId}`)
  return response
}

// 创建章节
export async function createChapter(data: {
  textbook_id: number
  chapter_number: number
  title: string
  content: string
}): Promise<Chapter> {
  const response = await apiClient.post<Chapter>('/v1/chapters/', data)
  return response
}

// 获取章节详情
export async function getChapterById(chapterId: number): Promise<Chapter> {
  const response = await apiClient.get<Chapter>(`/v1/chapters/${chapterId}`)
  return response
}

// 提交章节审核
export async function submitChapter(chapterId: number): Promise<{ message: string; chapter_id: number; status: string }> {
  const response = await apiClient.post(`/v1/chapters/${chapterId}/submit`)
  return response
}

// 审核章节
export async function reviewChapter(
  chapterId: number,
  data: { comment: string; status: 'approved' | 'rejected' | 'pending' }
): Promise<Review> {
  const response = await apiClient.post<Review>(`/v1/reviews/chapters/${chapterId}/review`, data)
  return response
}

// 发布章节
export async function publishChapter(chapterId: number): Promise<{ message: string; chapter_id: number; status: string }> {
  const response = await apiClient.post(`/v1/reviews/chapters/${chapterId}/publish`)
  return response
}

// 获取审核记录
export async function getReview(reviewId: number): Promise<Review> {
  const response = await apiClient.get<Review>(`/v1/reviews/${reviewId}`)
  return response
}

export async function downloadDocument(
  documentType: 'outline' | 'chapter' | 'book',
  format: 'markdown' | 'docx' | 'pdf' = 'markdown',
  options?: {
    taskId?: string
    chapterNumber?: number
    content?: string
    title?: string
    author?: string
    chapters?: Array<{
      chapterNumber: number
      chapterTitle: string
      chapterContent: string
      chapterMarkdown?: string
    }>
    outlineMarkdown?: string
  }
): Promise<Blob> {
  const blobConfig = {
    params: {
      format,
      taskId: options?.taskId,
      chapterNumber: options?.chapterNumber,
    },
    responseType: 'blob' as const,
    timeout: 300000,
  }

  if (options?.content || options?.chapters) {
    const response = await rawApiClient.post(
      `/v1/textbooks/writing/download/${documentType}`,
      {
        content: options.content,
        title: options.title,
        author: options.author,
        chapters: options.chapters,
        outlineMarkdown: options.outlineMarkdown,
      },
      blobConfig
    )
    return response.data as Blob
  }

  const response = await rawApiClient.get(
    `/v1/textbooks/writing/download/${documentType}`,
    blobConfig
  )
  return response.data as Blob
}

/** 经后端鉴权转发 n8n webhook（编写委员会成员） */
export async function postWritingN8nProxy<T = Record<string, unknown>>(
  workflowId: string,
  body: Record<string, unknown>
): Promise<T> {
  return apiClient.post(
    `/v1/textbooks/writing/n8n-proxy/${workflowId}`,
    body
  ) as Promise<T>
}

